import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../icons";

type Permiso = { id: number; modulo: string; accion: string; descripcion: string | null };
type Rol = { id: number; nombre: string; descripcion: string | null; sistema: boolean; permisos: Permiso[] };

// El backend ya guarda una descripción en español por permiso (tabla
// `permisos`, columna `descripcion`, sembrada desde permisos_catalogo.py) —
// antes esta pantalla la ignoraba y mostraba el código técnico "modulo:accion"
// crudo. Para el nombre del módulo (agrupador, no viene con descripción
// propia) se usa este mapa; un módulo nuevo que no esté aquí cae al
// fallback (código tal cual, capitalizado) en vez de romperse.
const MODULO_LABEL: Record<string, string> = {
  "core.empresas": "Configuración de empresa", "core.usuarios": "Usuarios", "core.roles": "Roles y permisos",
  "core.parametros": "Parámetros del sistema", "core.catalogos": "Catálogos", "core.auditoria": "Auditoría (bitácora)",
  compras: "Compras y Recepción", produccion: "Producción", costeo: "Costeo", inventarios: "Inventarios",
  comercial: "Comercial", gerencial: "Gerencial", contabilidad: "Contabilidad", nomina: "Nómina",
  cumplimiento: "Cumplimiento DIAN", pos: "POS (mostrador)", notas: "Notas crédito/débito", tesoreria: "Tesorería",
  activos: "Activos Fijos", restaurante: "Restaurante", dian: "Facturación DIAN", informes: "Informes y reportes",
  sigepro_ep: "SigeproEP — General", sigepro_ep_contabilidad: "SigeproEP — Contabilidad",
  sigepro_ep_tesoreria: "SigeproEP — Tesorería", sigepro_ep_presupuesto: "SigeproEP — Presupuesto",
  sigepro_ep_nomina: "SigeproEP — Nómina", sigepro_ep_activos: "SigeproEP — Activos Fijos",
  sigepro_ep_exogena: "SigeproEP — Información Exógena", sigepro_ep_territorial: "SigeproEP — Territorial (Predial/ICA)",
  sigepro_ep_informes: "SigeproEP — Informes",
};
function labelModulo(modulo: string): string {
  return MODULO_LABEL[modulo] || modulo.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Roles() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("core.roles:crear");
  const puedeEditar = tienePermiso("core.roles:editar");
  const puedeEliminar = tienePermiso("core.roles:eliminar");

  const [roles, setRoles] = useState<Rol[]>([]);
  const [permisos, setPermisos] = useState<Permiso[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<Rol | null>(null);

  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [permisoIds, setPermisoIds] = useState<number[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [r, p] = await Promise.all([api.get("/roles"), api.get("/permisos")]);
      setRoles(r);
      setPermisos(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const porModulo = permisos.reduce<Record<string, Permiso[]>>((acc, p) => {
    (acc[p.modulo] ||= []).push(p);
    return acc;
  }, {});

  const abrirNuevo = () => {
    setEditando(null);
    setNombre(""); setDescripcion(""); setPermisoIds([]);
    setFormError(null);
    setModalAbierto(true);
  };

  const abrirEditar = (r: Rol) => {
    setEditando(r);
    setNombre(r.nombre); setDescripcion(r.descripcion || "");
    setPermisoIds(r.permisos.map((p) => p.id));
    setFormError(null);
    setModalAbierto(true);
  };

  const togglePermiso = (id: number) => {
    setPermisoIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      if (editando) {
        await api.put(`/roles/${editando.id}`, { nombre, descripcion, permisoIds });
      } else {
        await api.post("/roles", { nombre, descripcion, permisoIds });
      }
      setModalAbierto(false);
      await cargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo guardar el rol.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (r: Rol) => {
    if (!window.confirm(`¿Eliminar el rol "${r.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.del(`/roles/${r.id}`);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar el rol.");
    }
  };

  return (
    <Shell titulo="Roles y permisos" breadcrumb="Core / Roles y permisos">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-textmuted">Control de acceso basado en roles (RBAC), definido en SIG-ARQ-001.</p>
        {puedeCrear && <button className="btn-primary" onClick={abrirNuevo}>+ Nuevo rol</button>}
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roles.map((r) => (
            <div key={r.id} className="card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    {r.nombre}
                    {r.sistema && <span className="badge bg-slate-100 text-slate-500">sistema</span>}
                  </div>
                  {r.descripcion && <div className="text-xs text-textmuted mt-0.5">{r.descripcion}</div>}
                </div>
                {!r.sistema && (
                  <div className="flex gap-2 shrink-0">
                    {puedeEditar && <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => abrirEditar(r)}>Editar</button>}
                    {puedeEliminar && <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => eliminar(r)}>Eliminar</button>}
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {r.permisos.length === 0 && <span className="text-xs text-textmuted">Sin permisos asignados.</span>}
                {r.permisos.map((p) => (
                  <span key={p.id} className="badge bg-navy/10 text-navy text-[10px]" title={`${labelModulo(p.modulo)} · ${p.modulo}:${p.accion}`}>
                    {p.descripcion || `${labelModulo(p.modulo)} — ${p.accion}`}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <form onSubmit={guardar} className="card w-full max-w-xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">{editando ? "Editar rol" : "Nuevo rol"}</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre del rol</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
              <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Permisos por módulo</label>
              <div className="space-y-3">
                {(Object.entries(porModulo) as [string, Permiso[]][]).map(([modulo, lista]) => (
                  <div key={modulo}>
                    <div className="text-[11px] font-semibold uppercase text-textmuted mb-1">{labelModulo(modulo)}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {lista.map((p: Permiso) => {
                        const marcado = permisoIds.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            className={`badge cursor-pointer border flex items-center gap-1 ${marcado ? "bg-accent text-white border-accent" : "bg-white border-borderc text-slate-600 hover:border-slate-400"}`}
                            title={`${p.modulo}:${p.accion}`}
                          >
                            <input type="checkbox" className="hidden" checked={marcado} onChange={() => togglePermiso(p.id)} />
                            {marcado
                              ? <Icon name="check" className="h-3 w-3 shrink-0" />
                              : <span className="h-3 w-3 shrink-0 rounded-sm border border-slate-300" />}
                            {p.descripcion || p.accion}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</button>
            </div>
          </form>
        </div>
      )}
    </Shell>
  );
}
